import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "@material-ui/core";
import { WorksOrderRequest } from "../../models/WorksOrders/WorksOrderRequest";
import * as api from "../../api";
import {
  clearMediaFiles,
  deleteMediaItem,
  resumeDraftRepair,
  userChosenDeletedMedia,
  userChosenUploadedMedia,
  userChosenVulnerabilityAdditionalInformation,
  userChosenVulnerabilityQuestion,
  userChosenCovidQuestion,
  userChosenVandalismAdditionalInformation,
  userChosenVandalismQuestion,
  hasMediaAttached,
  isEditState,
  fromMediaUpload,
  isRepairMediaResume,
  workOrderData,
  dmcCheckSavedAnswer,
  storeMediaFilesId,
} from "../../ducks/redux/actions/userChoices";
import Button from "../../Button/Button";
import ExitModalAndSave from "../Modal/ExitModalAndSave";
import classes from "./styles.module.css";
import styles from "./spinner.module.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/css/bootstrap.min.css";
import RepairJourney from "../RepairJourney";
import { RouteComponentProps } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { validateMediaSize } from "./mediaConfigure";
import MediaFileUploadForm from "./MediaFileUploadForm";
import MediaFilesTable from "./MediaFilesTable";

import { isNullOrUndefined } from "../../utils/formatting";
import { workOrderRepairObject } from "../../ducks/redux/actions/getAddress";
import { PermissionsList } from "../../models/Permissions";
import { useMediaUpload } from "../../hooks/useMediaUpload";
import { validateFileTypeAndSize } from "../../utils/mediaFileValidator";
import { savePreAssessmentReturnQuestion } from "../../ducks/redux/actions/hazardAssessment";
import { Helper } from "../../utils/Helper/HelperUtil";

const allowedExtensions = process.env.REACT_APP_ALLOW_EXTENSION as string;
const videoSize = process.env.REACT_APP_VIDEO_SIZE as string;

// Helper component for error/alert messages
interface ErrorAlertProps {
  messageKey: string;
  showBreak?: boolean;
}

const ErrorAlert: React.FC<ErrorAlertProps> = ({ messageKey, showBreak = false }) => {
  const { t } = useTranslation();
  return (
    <>
      <div className="error mt-4 pb-2">
        <div className="w-full px-2 py-2 bg-yellow flex items-center">
          <div className="px-4 pr-8">
            <i className="fas fa-exclamation-triangle text-brick-70 w-6 h-6"></i>
          </div>
          <div className="flex items-center text-black">
            <p className="w-full text-left my-2 font-AvantGardeGothic-Md">
              {t(messageKey)}
            </p>
          </div>
        </div>
      </div>
      {showBreak && <br />}
    </>
  );
};

interface MediaFilesType {
  name: string;
  size: number;
  fileType: string;
}
interface RepairUploadMediaProps extends RouteComponentProps<any> { }

const isHazardRepairExcluded = (asset: any, reason: any, isRecall: boolean): boolean =>
  Boolean(isRecall || asset?.statuses?.isInDefects || asset?.maintenanceResponsibilities?.some((responsibility: any) =>
    (responsibility.type === "MAINTSRESP" || responsibility.type === reason?.element) && responsibility.value === "EMAFULL"));

const RepairUploadMedia: React.FC<RepairUploadMediaProps> = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [closeAfterSaving, setCloseAfterSaving] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<MediaFilesType[]>([]);
  const [disableUploadButton, setDisableUploadButton] = useState(false);
  const [error, setError] = useState("");

  const {
    dragActive,
    fileTooLarge,
    wrongFileType,
    encodeImageFileAsURL,
    handleDrag,
    handleDrop,
    setFileTooLarge,
    setWrongFileType,
  } = useMediaUpload();

  let mediaLength = 0;
  let maxFileSize = +videoSize;
  const allowedConfigExtensions = allowedExtensions?.replace("[", "");
  const allowedExtensionsReplace = allowedConfigExtensions ? allowedConfigExtensions.split("'").join("") : "";
  const allowFileType = allowedExtensionsReplace?.replace("]", "");
  const EditState = useSelector(
    (state: any) => state?.userChoicesReducer?.isEditState
  );

  const inputRef = React.useRef<HTMLInputElement>(null);
  const assetByIdDataObject = useSelector(
    (state: any) =>
      state?.dashboardReducer?.formState?.assetByIdDataObject
  );
  const userChosenSOR = useSelector((state: any) => state?.userChoicesReducer?.userChosenSOR);

  const priorityCodeValue = useSelector((state: any) => state?.dashboardReducer?.formState?.priorityCode);
  const vandalismConfigValue = useSelector(
    (state: any) => state?.userChoicesReducer?.vandalismtoggleValue
  );

  const resumeDraftRepairVal = useSelector(
    (state: any) => state?.userChoicesReducer?.resumeDraftRepair
  );
  const dmcInspectionSorCodes = useSelector(
    (state: any) => state?.userChoicesReducer?.dmcInspectionSorCodeValues || []
  );

  const workOrderObject = useSelector(
    (state: any) => state?.addresses?.workOrderRepair
  );

  const userChosenUploadedMediaValue = useSelector(
    (state: any) => state?.userChoicesReducer?.userChosenUploadedMedia
  );

  const getPermissionsData = useSelector(
    (state: any) => state?.userChoicesReducer?.userProfilePermissions
  );

  const covidConfigValue = useSelector(
    (state: any) => state?.userChoicesReducer?.isRepairCovidAllowed
  );

  const hideVulnerablePagePermission = Array.isArray(getPermissionsData) && getPermissionsData.includes(PermissionsList.workoder_vulnerable_hide_permission);
  const isDmcEnabled = useSelector((state: any) => state?.userChoicesReducer?.isDMQuestionnaireEnabled);

  const customerIdentifier = useSelector(
    (state: any) => state?.addresses?.customerid
  );
  const userchosenDMC = useSelector((state: any) => state?.userChoicesReducer?.dmcCheckSavedAnswer);
  const userChosenHighLevelDescriptionBackButton = useSelector(
    (state: any) => state?.userChoicesReducer?.userChosenHighLevelDescription
  );

  const mediaIds = useSelector(
    (state: any) => state?.userChoicesReducer?.mediaItems
  );
  const isRepairMediaResumed = useSelector(
    (state: any) => state?.userChoicesReducer?.isRepairMediaResume
  );
  const DMCChecksavedAnswervalue = useSelector(
    (state: any) => state?.userChoicesReducer.dmcCheckSavedAnswer
  );
  const isHHSRSAssessmentCompleted = useSelector((state: any) => state?.hazard?.isHHSRSAssessmentCompleted === true);
  const selectedRepairReasonObject = useSelector((state: any) => state?.dashboardReducer?.formState?.selectedRepairReasonObject);
  const isHHSRSRepairEnabledCheck = useSelector((state: any) => state?.userChoicesReducer?.isHHSRSRepairEnabled);
  const hazardResponses = useSelector((state: any) => state?.hazard?.responses || []);
  const savedHazardQuestions = useSelector((state: any) => state.hazard?.hazardQuestions);
  const preAssessmentJourneyQuestions = useSelector((state: any) => state?.hazard?.preAssessmentJourneyQuestions || []);
  const activeHazardCasePresent = useSelector((state: any) => state?.hazard?.hasActiveHazardCase);
  const assessmentResponses = hazardResponses.filter(
    (item: any) => !item.isPreAssessment && item.answerCodes?.length
  );
  const preAssessmentResponses = hazardResponses.filter(
    (item: any) => item.isPreAssessment && item.answerCodes?.length
  );
  const currentRepairReason = selectedRepairReasonObject?.[0];
  const isRecall = useSelector((state: any) => state?.userChoicesReducer?.IsRecall);
  const isExcludedFromHazard = isHazardRepairExcluded(assetByIdDataObject, currentRepairReason, isRecall);
  // Apply the same eligibility checks on Back and on the forward journey.
  const isPreAssessmentBackNavigationEligible = Array.isArray(preAssessmentJourneyQuestions) && preAssessmentJourneyQuestions.length > 0;
  const isCurrentHazardJourney = !isExcludedFromHazard && savedHazardQuestions?.length > 0 && Helper.isHHSRSJourney({
    isPreAssessmentRequired: currentRepairReason?.isPreAssessmentRequired,
    isHazard: currentRepairReason?.isHazard,
    isPreAssessmentEligible: isPreAssessmentBackNavigationEligible,
    isHHSRSRepairEnabled: isHHSRSRepairEnabledCheck,
  });
  const isPreAssessmentBackJourney =
    !isExcludedFromHazard &&
    currentRepairReason?.isPreAssessmentRequired === true &&
    isPreAssessmentBackNavigationEligible &&
    isHHSRSRepairEnabledCheck === true;

  useEffect(() => {
    userChosenUploadedMediaValue?.forEach((mediaFile: any) => {
      if (mediaFile) {
        setMediaFiles(userChosenUploadedMediaValue);
      }
    });
    mediaLength = mediaIds?.length;
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    mediaLength = mediaIds?.length;
    if (mediaFiles?.length == 5) {
      setDisableUploadButton(true);
    }
  }, [mediaFiles]);

  // === Displaying media while user is coming from resume work order ===
  useEffect(() => {

    if (isRepairMediaResumed) {
      dispatch(isRepairMediaResume(false));
      resumeWorkOrderMedia();
    }
  }, []);

  const processDocumentMetadata = (data: any) => {
    data?.metadata?.forEach((res: any) => {
      if (res.key === "content-type") {
        const name = data.media.fileName;
        const fileType = res.value;
        dispatch(userChosenUploadedMedia({ name, fileType }));
        setMediaFiles((currentMediaFiles: any) => [
          ...currentMediaFiles,
          { name, fileType },
        ]);
      }
    });
  };

  const resumeWorkOrderMedia = async () => {
    try {
      setIsLoading(true);
      const { documents } = await api.getRepairMediaData(workOrderObject?.id);
      documents?.forEach((data: any) => {
        dispatch(storeMediaFilesId(data.id));
        processDocumentMetadata(data);
      });
    } catch {
      setError("Media_SSR011c");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      UpdateWorkOrder();
    }, 1000);
    return () => clearTimeout(timer);
  }, [mediaIds?.length]);

  const backButtonHandler = () => {
    if (isHHSRSAssessmentCompleted && isCurrentHazardJourney && assessmentResponses.length && !activeHazardCasePresent) {
      props.history.push("/HazardReview");
    } else if (isPreAssessmentBackJourney && preAssessmentResponses.length) {
      const lastPreAssessmentResponse = [...preAssessmentResponses]
        .sort((first: any, second: any) => Number(second.sequence) - Number(first.sequence))?.[0];
      dispatch(savePreAssessmentReturnQuestion({
        code: lastPreAssessmentResponse?.code || "",
        from: "RepairUploadMedia",
      }));
      props.history.push("/HazardPreassessment");
    } else if ((assetByIdDataObject?.assetType === "Property" && (dmcInspectionSorCodes.includes(userChosenSOR))) || (userchosenDMC === "Yes" && isDmcEnabled)) {
      props.history.push("/DampAndMouldAssesment/3");
    } else if (userchosenDMC === "No" && isDmcEnabled) {
      props.history.push("/ConfirmedDampMould");
    } else if (isCurrentHazardJourney && !activeHazardCasePresent) {
      props.history.push(`/HazardReview`);
    } else {
      props.history.push("/repairadditionaldetails");
    }
  };

  const exitButtonHandler = () => {
    dispatch(clearMediaFiles([]));
    dispatch(dmcCheckSavedAnswer(""));
    props.history.push("/LandingView");
  };

  const UpdateWorkOrder = async () => {
    workOrderObject.priority = priorityCodeValue;
    const _worksOrderRequest = new WorksOrderRequest(workOrderObject);
    let woRequestParameters =
      _worksOrderRequest.updateWorkOrderParameter(workOrderObject);
    woRequestParameters.documents = {
      documentIdentifiers: mediaIds,
    };
    workOrderObject.documents = {
      documentIdentifiers: mediaIds,
    };
    try {
      await api.updateWorkOrderRepairMedia(
        woRequestParameters,
        workOrderObject.id,
        "eTag"
      );
      const worksOrder = new WorksOrderRequest(workOrderObject);
      dispatch(workOrderData(worksOrder))
      dispatch(workOrderRepairObject(workOrderObject));
    } catch {
      props.history.push("/genericerror");
    }
  };

  const saveDraftAndExit = () => {
    // === update store for resume draft ===
    dispatch(fromMediaUpload(true));
    setCloseAfterSaving(true);
    props.history.push(`/WorkOrder360View/${workOrderObject.id}`)

  };

  const routingCheck = () => {
    if (assetByIdDataObject?.statuses?.isInDefects === true && vandalismConfigValue === true) {
      props.history.push("/vandalismquestion");
    } else {
      const hideVulnerablePage = Helper.shouldHideVulnerabilityPage({
        hasHideVulnerabilityPermission: hideVulnerablePagePermission,
        isEmergency: workOrderObject?.isEmergency,
        isHazard: currentRepairReason?.isHazard,
        isHHSRSRepairEnabled: isHHSRSRepairEnabledCheck,
      });
      if (hideVulnerablePage) {
        if (covidConfigValue) {
          props.history.push("/CovidQuestion");
        }
        else {
          props.history.push("/CustomerDetails");
        }
      }
      else {
        props.history.push("/vulnerabilityquestion");
      }
    }
  }

  const onContinueHandler = () => {
    dispatch(fromMediaUpload(false));
    // === Validating media has attached ===
    if (mediaIds?.length > 0) {
      dispatch(hasMediaAttached("Yes"));
    } else {
      dispatch(hasMediaAttached("No"));
    }

    if (EditState === true && mediaLength !== mediaIds.length) {
      dispatch(resumeDraftRepair(false));
      dispatch(userChosenVandalismQuestion("No"));
      dispatch(userChosenVandalismAdditionalInformation(""));
      dispatch(userChosenCovidQuestion(""));
      dispatch(userChosenVulnerabilityAdditionalInformation(""));
      dispatch(isEditState(false));
    }
    if (resumeDraftRepairVal) {
      dispatch(resumeDraftRepair(false));
      if (!workOrderObject?.assessmentInformation?.identifier && !workOrderObject?.assessmentIdentifier) {
        dispatch(userChosenCovidQuestion(""));
        dispatch(userChosenVulnerabilityQuestion("No"));
        dispatch(userChosenVulnerabilityAdditionalInformation(""));
      }
      dispatch(isEditState(false));
    }
    routingCheck();
  };



  const processMediaFile = async (
    uploadMediaArray: any,
    name: any,
    image: any,
    fileType: any
  ) => {
    const size = validateMediaSize(fileType);

    const validation = validateFileTypeAndSize(
      fileType,
      uploadMediaArray[0].size,
      allowedExtensions,
      size
    );

    const isInvalidType = !validation.isValidType;
    const isInvalidSize = !validation.isValidSize;

    if (isInvalidType) {
      setFileTooLarge(false);
      setWrongFileType(true);
      setIsLoading(false);
    } else if (isInvalidSize) {
      setWrongFileType(false);
      setFileTooLarge(true);
      setIsLoading(false);
    } else {
      setFileTooLarge(false);
      setWrongFileType(false);
      let mediaParam = {};
      // === Upload media files on server ===
      const isCustomerIdentifierDefined = !isNullOrUndefined(customerIdentifier);
      if (isCustomerIdentifierDefined) {
        mediaParam = {
          DocumentType: "WORKORDER",
          Media: image?.substr(image?.indexOf(",") + 1),
          "customer-identifier": customerIdentifier,
          Metadata: [
            {
              key: "asset-identifier",
              value: assetByIdDataObject.id,
            },
            {
              key: "works-order-identifier",
              value: workOrderObject.id,
            },
            {
              key: "filename",
              value: uploadMediaArray[0].name,
            },

            {
              key: "content-type",
              value: fileType,
            },
          ],
        };
      }
      else {
        mediaParam = {
          DocumentType: "WORKORDER",
          Media: image?.substr(image?.indexOf(",") + 1),
          Metadata: [
            {
              key: "asset-identifier",
              value: assetByIdDataObject.id,
            },
            {
              key: "works-order-identifier",
              value: workOrderObject.id,
            },
            {
              key: "filename",
              value: uploadMediaArray[0].name,
            },

            {
              key: "content-type",
              value: fileType,
            },
          ],
        };
      }


      try {
        setIsLoading(true);
        const response = await api.postRepairMediaFile(
          workOrderObject.id,
          mediaParam
        );

        // ===== Updating patch work work when media uploaded successfully ====
        dispatch(storeMediaFilesId(response.documentIdentifier));
        dispatch(userChosenUploadedMedia({ name, fileType }));
        setMediaFiles((currentMediaFiles: any) => [
          ...currentMediaFiles,
          { name, fileType },
        ]);
      } catch {
        setError("Media_SSR011c");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleMediaFilesChange = (event: any) => {
    event.preventDefault();
    if (event.target.files?.[0]) {
      setError("");
      setFileTooLarge(false);
      setWrongFileType(false);
      setIsLoading(true);
      encodeImageFileAsURL(event.target.files, processMediaFile);
    }
  };

  const deleteMediaFile = async (id: number) => {
    setFileTooLarge(false);
    setWrongFileType(false);
    setError("");

    try {
      setIsLoading(true);
      await api.deleteRepairMediaFiles(workOrderObject.id, mediaIds[id]);
      dispatch(deleteMediaItem(mediaIds[id]));
      // === Delete media files from database ===
      const updatedMedia = mediaFiles.filter(
        (mediaFile: any, index: number) => {
          return index !== id;
        }
      );
      setMediaFiles(updatedMedia);
      dispatch(userChosenDeletedMedia(updatedMedia));
      setDisableUploadButton(false);
    } catch {
      setError("Media_SSR011b");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDropWrapper = (event: React.DragEvent) => {
    handleDrop(event, (files) => {
      if (mediaFiles.length < 5) {
        setError("");
        setFileTooLarge(false);
        setWrongFileType(false);
        setIsLoading(true);
        encodeImageFileAsURL(files, processMediaFile);
      }
    });
  };

  const onButtonClick = () => {
    inputRef.current!.click();
  };

  return (
    <Container id="supp-main-container" role="main">

      <div id="backmodal-div1" className="mx-auto mb-0 md:mb-14 lg:mb-10 px-0 xl:px-0" >
        <div className="back-and-journey">
          <p id="backmodal-p" className="text-teal text-xl mt-4 mb-8 sm-pl-1">
            <button data-testid="custBackBtn" className="back-btn" aria-label={t("back")} onClick={backButtonHandler}>
              <i className="fas fa-angle-left mr-2"></i>
              {t("back")}
            </button>
          </p>
          <div className=""></div>
          {<RepairJourney />}
        </div>
      </div>

      {isLoading && (
        <div className={classes.overlay}>
          <div id="UploadingOverlay">
            <div className={styles.overlayContainer}>
              <div className={styles.overlayContent}>
                <div className={styles.spinner}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="question ml-sm-5 pl-sm-5 repair-text">
        {(DMCChecksavedAnswervalue === "No" || DMCChecksavedAnswervalue === "") && <div className="py-2 h5 mar-repadd">
          <h1
            data-testid="Upload_Media_Title"
            className="text-purple-100 text-2xl rep-mar global-txt mb-0"
          >
            {t("Repair_Upload_Media_Title")}
          </h1>
        </div>}
        {DMCChecksavedAnswervalue === "Yes" && <div> <div className="py-2 h5 mar-repadd flex">
          <div className={classes.flexx}><h1
            data-testid="Upload_Media_Title"
            className="text-purple-100 text-2xl rep-mar global-txt mb-0"
          >
            {t("Repair_Upload_Media_Title_DMC")}
          </h1></div>
          <div className={classes.flex}><h1
            data-testid="Upload_Media_Title"
            className="text-purple-100 text-2xl rep-mar ml-1 global-txt mb-0"
          >
            <strong>{userChosenHighLevelDescriptionBackButton}</strong>
          </h1></div>
        </div>
          <div className="py-2 h5 mar-repadd">
            <h1
              data-testid="Upload_Media_Title"
              className="text-purple-100 text-2xl mt-0 rep-mar global-txt"
            >
              {t("Repair_Upload_Media_Title_DMC3")}
            </h1>
          </div>
        </div>}
        <div>
          <p className="text-gray-700 text-black global-txt fw-light">
            {t("Reapir_Upload_Media_SubHeading_Attachment")}
          </p>
          <p className="text-gray-700 text-black global-txt fw-light">
            {`${t("Upload_Media_SubHeading2")} ${allowFileType}`}
          </p>
        </div>
        <div className="w-full">
          <MediaFileUploadForm
            dragActive={dragActive}
            disableUploadButton={disableUploadButton}
            inputRef={inputRef}
            handleDrag={handleDrag}
            handleMediaFilesChange={handleMediaFilesChange}
            handleDropWrapper={handleDropWrapper}
            onButtonClick={onButtonClick}
          />

          <div className=" flex-col md:flex-row md:justify-between">
            <div className="text-gray-700 text-black global-txt fw-light">
              {`${t("Upload_Media_Initial_Text3")} ${maxFileSize / 1000000}MB`}
            </div>
            <div
              className={`${classes.uploadMediaNo} text-gray-700 text-black global-txt fw-light text-right`}
            >
              {mediaFiles.length}/5
            </div>
          </div>

          <div className="error mt-4 pb-2">
            <div className="w-full px-2 py-2 bg-purple-20 flex items-center">
              <div className="px-4 pr-8">
                <i className="fas fa-bullhorn text-purple-100 w-6 h-6"></i>
              </div>
              <div className="flex items-center text-black">
                <p className="w-full text-left my-2 font-AvantGardeGothic-Md">
                  {t("Upload_Media_Initial_Alert_1")} <br /><br />
                  {t("Upload_Media_Initial_Alert_2")}
                </p>
              </div>
            </div>
          </div>

          {fileTooLarge && <ErrorAlert messageKey="Repair_Upload_Media_Attachments_Too_Large" showBreak />}
          {error && <ErrorAlert messageKey={error} />}
          {wrongFileType && <ErrorAlert messageKey="Upload_Media_Wrong_File_Type" showBreak />}
          {mediaFiles.length >= 5 && <ErrorAlert messageKey="Upload_Media_Max_Attachments" />}

          <MediaFilesTable
            mediaFiles={mediaFiles}
            deleteMediaFile={deleteMediaFile}
            props={props}
          />
        </div>
      </div>

      <div
        className="flex justify-between items-center pb-4 md:pb-8"
        id="supp-main-btn"
      >
        <ExitModalAndSave
          id="supp-exitmodal"
          exit={exitButtonHandler}
          saveDraftAndExit={saveDraftAndExit}
          closeAfterSaving={closeAfterSaving}
        />
        <Button
          id="supp-btn1"
          onClick={onContinueHandler}
          className="button-class"
        >
          {t("continuen_button")}
          <i className="fas fa-angle-right relativetext-white pointer-events-none arrow"></i>
        </Button>
      </div>
    </Container>
  );
};
export default RepairUploadMedia;
